import React, { useEffect, useState } from 'react';
import { Upload, Button, Input, Card, Space, Typography, message, Row, Col, Divider, Form, Spin } from 'antd';
import {
  UploadOutlined,
  FileTextOutlined,
  DeleteOutlined,
  EditOutlined,
  CloudUploadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface';
import { uploadLabResult } from '@app/api/labs.api';

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;

interface DocumentUploadProps {
  isViewMode?: boolean;
  labId?: number | null;
  deptId?: number | null;
  form?: any;
  resultParameters?: any;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  isViewMode = false,
  labId,
  deptId,
  form,
  resultParameters,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const serviceCatId = resultParameters?.lab_request_service_category_id;
  console.log('passsed resultParameters:', resultParameters);

  useEffect(() => {
    if (resultParameters?.result_report && resultParameters?.has_test_result_attachment) {
      const existingFile: UploadFile = {
        uid: 'existing-file',
        name: resultParameters.result_report,
        status: 'done',
        url: resultParameters.result_report_url,
      };
      setFileList([existingFile]);
    }
  }, [resultParameters]);

  const handleViewFile = () => {
    const currentFile = fileList[0];
    let fileUrl = null;

    if (currentFile) {
      if (currentFile.url) {
        fileUrl = currentFile.url;
      } else if (currentFile.originFileObj) {
        fileUrl = URL.createObjectURL(currentFile.originFileObj);
      } else if (resultParameters && resultParameters.result_report_url) {
        fileUrl = resultParameters.result_report_url;
      }
    }

    if (fileUrl) {
      window.open(fileUrl, '_blank');
    } else {
      message.error('File URL not available');
    }
  };

  const handleUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;

    if (!labId || !deptId || !serviceCatId) {
      const errorMsg = 'Missing required parameters: labId, deptId, or serviceCatId';
      message.error(errorMsg);
      onError?.(new Error(errorMsg));
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      const finalFileName = fileName.trim() || (file as File).name;

      const fileBlob = new File([file as File], finalFileName, { type: (file as File).type });
      formData.append('file', fileBlob);

      const response = await uploadLabResult(labId, deptId, serviceCatId, formData);

      const uploadFile: UploadFile = {
        uid: Date.now().toString(),
        name: finalFileName,
        status: 'done',
        originFileObj: file as RcFile,
        response: response,
      };

      setFileList([uploadFile]);
      setIsEditing(false);
      setUploading(false);

      onSuccess?.(response);
      message.success(`${finalFileName} uploaded successfully`);

      setFileName('');
    } catch (error: any) {
      setUploading(false);
      console.error('Upload error:', error);

      const errorMessage = error.message || 'Upload failed';
      message.error(errorMessage);
      onError?.(error);
    }
  };

  const handleRemove = () => {
    setFileList([]);
    setFileName('');
    message.success('Document removed successfully');
  };

  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSaveFileName = () => {
    if (fileList.length > 0 && fileName.trim()) {
      const updatedFile = { ...fileList[0], name: fileName.trim() };
      setFileList([updatedFile]);
      setIsEditing(false);
      message.success('File name updated successfully');
    }
  };

  const uploadProps: UploadProps = {
    customRequest: handleUpload,
    fileList,
    showUploadList: false,
    beforeUpload: (file) => {
      const isValidType =
        file.type === 'application/pdf' ||
        file.type.startsWith('image/') ||
        file.type === 'application/msword' ||
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

      if (!isValidType) {
        message.error('Only PDF, Word documents, and images are allowed');
        return false;
      }

      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('File must be smaller than 2');
        return false;
      }

      return true;
    },
    disabled: isViewMode || uploading,
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <Title level={5} style={{ marginBottom: 24, color: '#1890ff' }}>
        <FileTextOutlined style={{ marginRight: 8 }} />
        Upload Results as a Document
      </Title>

      {fileList.length === 0 ? (
        <div>
          {/* {!isViewMode && !uploading && (
            <div style={{ marginBottom: 16 }}>
              <Row gutter={[16, 0]} align="middle">
                <Col span={18}>
                  <Input
                    placeholder="Enter document name (optional)"
                    value={fileName}
                    onChange={handleFileNameChange}
                    prefix={<EditOutlined />}
                    onClick={(e) => e.stopPropagation()}
                    //size='small'
                  />
                </Col>
                <Col span={6}>
                  <Upload {...uploadProps} showUploadList={false}>
                    <Button
                      type="primary"
                      icon={<UploadOutlined />}
                      loading={uploading}
                      disabled={uploading}
                      block
                      //size="small"
                    >
                      Choose File
                    </Button>
                  </Upload>
                </Col>
              </Row>
            </div>
          )} */}

          {uploading ? (
            <div
              style={{
                border: '2px dashed #d9d9d9',
                borderRadius: '8px',
                backgroundColor: '#fafafa',
                textAlign: 'center',
                padding: '40px 20px',
              }}
            >
              <Spin size="large" />
              <div style={{ marginTop: 12 }}>
                <Text type="secondary">Uploading, please wait...</Text>
              </div>
            </div>
          ) : (
            <Dragger
              {...uploadProps}
              style={{
                border: '2px dashed #d9d9d9',
                borderRadius: '8px',
                backgroundColor: '#fafafa',
                textAlign: 'center',
                padding: '40px 20px',
                transition: 'all 0.3s ease',
                cursor: isViewMode ? 'not-allowed' : 'pointer',
              }}
              //bodyStyle={{ padding: 0 }}
              //   hoverable={!isViewMode}
            >
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <CloudUploadOutlined
                  style={{
                    fontSize: '48px',
                    color: isViewMode ? '#bfbfbf' : '#1890ff',
                    opacity: isViewMode ? 0.5 : 1,
                  }}
                />

                <div>
                  <Text strong style={{ fontSize: '16px', display: 'block', marginBottom: 8 }}>
                    {isViewMode ? 'Document Upload Disabled' : 'Upload Results Document'}
                  </Text>
                  <Text type="secondary">
                    {isViewMode ? 'View mode is active' : 'Drag and drop or click to upload PDF, Word, or Image files'}
                  </Text>
                </div>

                {!isViewMode && (
                  <div>
                    <Row gutter={[16, 16]} justify="center" align="middle">
                      {/* <Col span={24} style={{ maxWidth: '400px' }}>
                      <Input
                        placeholder="Enter document name (optional)"
                        value={fileName}
                        onChange={handleFileNameChange}
                        prefix={<EditOutlined />}
                        size="small"
                        // style={{ marginBottom: 16 }}
                      />
                    </Col> */}
                      <Col>
                        <Upload {...uploadProps}>
                          <Button
                            type="primary"
                            icon={<UploadOutlined />}
                            size="small"
                            loading={fileList.some((file) => file.status === 'uploading')}
                          >
                            Upload
                          </Button>
                        </Upload>
                      </Col>
                    </Row>

                    <Text type="secondary" style={{ fontSize: '12px', marginTop: 12, display: 'block' }}>
                      Supported formats: PDF, JPG, PNG, (Max size: 2MB)
                    </Text>
                  </div>
                )}
              </Space>
            </Dragger>
          )}
        </div>
      ) : (
        // Uploaded File Display
        <Card
          style={{
            borderRadius: '8px',
            border: '1px solid #e8f4fd',
            backgroundColor: '#f6ffed',
          }}
        >
          <Row gutter={[16, 16]} align="middle">
            <Col flex="auto">
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FileTextOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
                  {isEditing ? (
                    <Space>
                      <Input
                        value={fileName}
                        onChange={handleFileNameChange}
                        onPressEnter={handleSaveFileName}
                        autoFocus
                        style={{ width: '300px' }}
                      />
                      <Button type="primary" size="small" onClick={handleSaveFileName}>
                        Save
                      </Button>
                      <Button size="small" onClick={() => setIsEditing(false)}>
                        Cancel
                      </Button>
                    </Space>
                  ) : (
                    <>
                      <Text strong style={{ fontSize: '16px' }}>
                        {fileList[0].name}
                      </Text>
                    </>
                  )}
                </div>
                {/* <Text type="secondary" style={{ fontSize: '12px' }}>
                  File size: {((fileList[0].originFileObj?.size || 0) / 1024 / 1024).toFixed(2)} MB
                </Text> */}
              </Space>
            </Col>
            <Col>
              <Space>
                {/* View Button - Always show if file exists */}
                <Button type="primary" icon={<EyeOutlined />} onClick={handleViewFile} size="small">
                  View
                </Button>

                {!isViewMode && (
                  <>
                    <Upload {...uploadProps}>
                      <Button icon={<UploadOutlined />} size="small">
                        Replace
                      </Button>
                    </Upload>
                    <Button danger icon={<DeleteOutlined />} onClick={handleRemove} size="small">
                      Remove
                    </Button>
                  </>
                )}
              </Space>
            </Col>
          </Row>
        </Card>
      )}
    </div>
  );
};

export default DocumentUpload;
